import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import DetailLayout from './DetailLayout';
import {
  getFilteredAndSortedRecords,
  getListViewFieldDisplayText,
} from './utils/listViewUtils';
import './css/ListDetailView.css';

const MAX_OPEN_TABS = 15;

const getRecordId = (record) => record?.id?.value ?? record?.id ?? null;

/**
 * List + detail split view: record list on the left, tabbed detail panels on the right.
 */
const ListDetailView = ({
  objectName,
  records = [],
  fieldOrder = [],
  fieldDataTypes = {},
  searchTerm = '',
  filters = {},
  sortBy = '',
  sortOrder = 'asc',
  loading = false,
  socket,
  selectedWhatsAppSetting,
  onShowToast,
}) => {
  const [openTabs, setOpenTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);

  const recordLinkField = useMemo(() => {
    if (!fieldOrder || fieldOrder.length === 0) {
      return 'name';
    }
    if (fieldOrder.includes('name')) {
      return 'name';
    }
    return fieldOrder[0];
  }, [fieldOrder]);

  const subtitleField = useMemo(
    () => fieldOrder.find((field) => field !== recordLinkField && field !== 'id') || null,
    [fieldOrder, recordLinkField]
  );

  const getRecordFieldText = useCallback(
    (record, fieldName) => {
      if (!record || !fieldName || record[fieldName] == null) {
        return '';
      }
      return getListViewFieldDisplayText(record[fieldName], fieldName, fieldDataTypes);
    },
    [fieldDataTypes]
  );

  const getRecordTitle = useCallback(
    (record) => {
      if (!record) {
        return 'Untitled';
      }
      return getRecordFieldText(record, recordLinkField) || 'Untitled';
    },
    [getRecordFieldText, recordLinkField]
  );

  const filteredRecords = useMemo(
    () =>
      getFilteredAndSortedRecords(
        records,
        searchTerm,
        filters,
        sortBy,
        sortOrder,
        fieldDataTypes
      ),
    [records, searchTerm, filters, sortBy, sortOrder, fieldDataTypes]
  );

  const openTabIds = useMemo(() => new Set(openTabs.map((tab) => tab.id)), [openTabs]);

  const openTab = useCallback(
    (recordId, title) => {
      if (!recordId) {
        return;
      }
      setOpenTabs((prev) => {
        const exists = prev.some((tab) => tab.id === recordId);
        if (exists) {
          setActiveTabId(recordId);
          if (!title) {
            return prev;
          }
          return prev.map((tab) =>
            tab.id === recordId ? { ...tab, title } : tab
          );
        }
        if (prev.length >= MAX_OPEN_TABS) {
          onShowToast?.(
            `You can open at most ${MAX_OPEN_TABS} records at once. Close a tab to open another.`,
            'warning'
          );
          return prev;
        }
        setActiveTabId(recordId);
        return [...prev, { id: recordId, title: title || 'Untitled' }];
      });
    },
    [onShowToast]
  );

  const closeTab = useCallback((recordId, event) => {
    event?.stopPropagation();
    event?.preventDefault();
    setOpenTabs((prev) => {
      const nextTabs = prev.filter((tab) => tab.id !== recordId);
      setActiveTabId((currentActive) => {
        if (currentActive !== recordId) {
          return currentActive;
        }
        if (nextTabs.length === 0) {
          return null;
        }
        const closedIndex = prev.findIndex((tab) => tab.id === recordId);
        const nextIndex = Math.min(closedIndex, nextTabs.length - 1);
        return nextTabs[nextIndex]?.id ?? null;
      });
      return nextTabs;
    });
  }, []);

  useEffect(() => {
    if (filteredRecords.length === 0) {
      setOpenTabs([]);
      setActiveTabId(null);
      return;
    }
    const validIds = new Set(filteredRecords.map((record) => getRecordId(record)));
    setOpenTabs((prev) => {
      const synced = prev
        .filter((tab) => validIds.has(tab.id))
        .map((tab) => {
          const record = filteredRecords.find((r) => getRecordId(r) === tab.id);
          return {
            id: tab.id,
            title: record ? getRecordTitle(record) : tab.title,
          };
        });
      if (synced.length > 0) {
        return synced;
      }
      const first = filteredRecords[0];
      const firstId = getRecordId(first);
      return [{ id: firstId, title: getRecordTitle(first) }];
    });
    setActiveTabId((prev) => {
      if (prev && validIds.has(prev)) {
        return prev;
      }
      return getRecordId(filteredRecords[0]);
    });
  }, [filteredRecords, getRecordTitle]);

  return (
    <div
      className="listview-detail-split"
      style={{ flex: 1, minWidth: 0, width: '100%' }}
    >
      <aside className="listview-detail-sidebar">
        <div className="listview-detail-sidebar-header">
          {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
        </div>
        <div className="listview-detail-sidebar-list">
          {loading && filteredRecords.length === 0 ? (
            <div className="listview-detail-empty" style={{ minHeight: '200px' }}>
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="ms-2">Loading records...</span>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="listview-detail-empty" style={{ minHeight: '200px' }}>
              No records found
            </div>
          ) : (
            filteredRecords.map((record) => {
              const recordId = getRecordId(record);
              const title = getRecordFieldText(record, recordLinkField) || 'Untitled';
              const subtitle = subtitleField
                ? getRecordFieldText(record, subtitleField)
                : '';
              const isActive = activeTabId === recordId;
              const isOpen = openTabIds.has(recordId);
              return (
                <button
                  key={recordId}
                  type="button"
                  className={`listview-detail-item${isActive ? ' active' : ''}${isOpen && !isActive ? ' open' : ''}`}
                  onClick={() => openTab(recordId, title)}
                >
                  <div className="listview-detail-item-title" title={title}>
                    {title}
                  </div>
                  {subtitle ? (
                    <div className="listview-detail-item-subtitle" title={subtitle}>
                      {subtitle}
                    </div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </aside>

      <div className="listview-detail-panel">
        {openTabs.length > 0 ? (
          <>
            <div className="listview-detail-tabs" role="tablist" aria-label="Open records">
              {openTabs.map((tab) => {
                const isTabActive = activeTabId === tab.id;
                return (
                  <div
                    key={tab.id}
                    role="tab"
                    aria-selected={isTabActive}
                    className={`listview-detail-tab${isTabActive ? ' active' : ''}`}
                  >
                    <button
                      type="button"
                      className="listview-detail-tab-label"
                      title={tab.title}
                      onClick={() => setActiveTabId(tab.id)}
                    >
                      {tab.title}
                    </button>
                    <button
                      type="button"
                      className="listview-detail-tab-close"
                      title="Close tab"
                      aria-label={`Close ${tab.title}`}
                      onClick={(e) => closeTab(tab.id, e)}
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="listview-detail-tab-content">
              {openTabs.map((tab) => (
                <div
                  key={`${objectName}-${tab.id}`}
                  className={`listview-detail-tab-pane${activeTabId === tab.id ? '' : ' hidden'}`}
                  role="tabpanel"
                  hidden={activeTabId !== tab.id}
                  aria-hidden={activeTabId !== tab.id}
                >
                  <DetailLayout
                    objectName={objectName}
                    id={tab.id}
                    socket={socket}
                    selectedWhatsAppSetting={selectedWhatsAppSetting}
                    embedOptions={{
                      isLayoutEmbed: true,
                      isTabActive: activeTabId === tab.id,
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="listview-detail-empty">Select a record to view details</div>
        )}
      </div>
    </div>
  );
};

export default ListDetailView;
